import React, {useCallback, useState} from 'react';

import {AssignmentsList} from 'client/TeacherDashboard/Assignments/AssignmentsList';
import {SkillPathsList} from 'client/TeacherDashboard/SkillPaths/SkillPathsList';
import {ClassContentReadyProvider} from 'client/TeacherDashboard/ClassDetail/useClassContentReady';
import {ClassDetailEntryFlows} from 'client/TeacherDashboard/ClassDetail/ClassDetailEntryFlows';
import {
  ClassDetailContentSkeleton,
  ClassHeaderSkeleton
} from 'client/TeacherDashboard/ClassDetail/ClassDetailSkeleton';
import {Overview} from 'client/TeacherDashboard/ClassDetail/Overview/Overview';
import {People} from 'client/TeacherDashboard/ClassDetail/People/People';
import {Settings} from 'client/TeacherDashboard/ClassDetail/Settings/Settings';
import {ClassSubjects} from 'client/TeacherDashboard/ClassDetail/Subjects/ClassSubjects';
import {useClassPageReady} from 'client/TeacherDashboard/ClassDetail/useClassPageReady';
import {useStoreChangePump} from 'client/TeacherDashboard/ClassDetail/useStoreChangePump';
import {DashboardHeader} from 'client/TeacherDashboard/Layout/DashboardHeader';
import {TeacherDashboardLayout} from 'client/TeacherDashboard/Layout/TeacherDashboardLayout';

import LoadingIndicator from 'generic/LoadingIndicator.react';

import Classrooms from './Classrooms.react';
import SecondaryHeader from './shared/SecondaryHeader';

interface Props {
  children?: React.ReactNode;
  location?: {pathname?: string};
  params?: {classId?: string};
  [routeProp: string]: unknown;
}

const CLASS_ASSIGNMENTS_PATH = /^\/classes\/[^/]+\/assignments\/?$/;
// Anchored to the class overview only — must NOT swallow the per-student /students/detail/:id route.
const CLASS_OVERVIEW_PATH = /^\/classes\/[^/]+\/students\/overview\/?$/;
const CLASS_SETTINGS_PATH = /^\/classes\/[^/]+\/settings(?:\/|$)/;
// Subtree form so the legacy /people/students/{enrolled,invited} + /coteachers/approved sub-paths
const CLASS_PEOPLE_PATH = /^\/classes\/[^/]+\/people(?:\/|$)/;
const CLASS_SUBJECTS_PATH = /^\/classes\/[^/]+\/subjects\/?$/;
const CLASS_SKILL_PATHS_PATH = /^\/classes\/[^/]+\/skill-paths\/?$/;
// The student my-assignments view stays on legacy chrome.
const STUDENT_ASSIGNMENTS_PATH = /\/my-assignments\/?$/;

// v2-rebuilt tabs render inline, keyed per class; ClassDetailEntryFlows (below) awaits the classroom once.
const INLINE_TABS: {
  match: RegExp;
  render: (classroomId: string) => React.ReactNode;
  gatesContent?: boolean;
}[] = [
  {
    match: CLASS_ASSIGNMENTS_PATH,
    render: (classroomId) => <AssignmentsList classroomId={classroomId} />,
    gatesContent: true
  },
  {
    match: CLASS_OVERVIEW_PATH,
    render: (classroomId) => <Overview classroomId={classroomId} key={`overview-${classroomId}`} />,
    gatesContent: true
  },
  {
    match: CLASS_SETTINGS_PATH,
    render: (classroomId) => <Settings classroomId={classroomId} key={`settings-${classroomId}`} />,
    gatesContent: true
  },
  {
    match: CLASS_PEOPLE_PATH,
    render: (classroomId) => <People classroomId={classroomId} key={`people-${classroomId}`} />,
    gatesContent: true
  },
  {
    match: CLASS_SUBJECTS_PATH,
    render: (classroomId) => (
      <ClassSubjects classroomId={classroomId} key={`subjects-${classroomId}`} />
    ),
    gatesContent: true
  },
  {
    match: CLASS_SKILL_PATHS_PATH,
    render: (classroomId) => (
      <SkillPathsList classroomId={classroomId} key={`skill-paths-${classroomId}`} />
    ),
    gatesContent: true
  }
];

// Re-renders a passive-Mandark legacy tab on store changes; the fresh clone each tick defeats
// React's stable-children bailout at the router boundary.
const ClassDetailContent = ({children}: {children: React.ReactNode}) => {
  useStoreChangePump();
  return (
    <div className='pt-x4'>
      {React.isValidElement(children) ? React.cloneElement(children) : children}
    </div>
  );
};

interface ClassDetailChromeProps {
  classroomId: string;
  pathname: string;
  inlineTab?: (typeof INLINE_TABS)[number];
  children: React.ReactNode;
}

// The header and the Assignments tab own their skeletons; other tabs show a generic one until the
// banner loads. A gatesContent tab (Assignments) reports when its content is painted, so the header
// holds its skeleton until then and the banner + content reveal together instead of the header first.
const ClassDetailChrome = ({
  classroomId,
  pathname,
  inlineTab,
  children
}: ClassDetailChromeProps) => {
  const bannerReady = useClassPageReady(classroomId);
  const gatesContent = Boolean(inlineTab?.gatesContent);

  // Sticky per class: once the tab reports, the page stays revealed across tab switches.
  const [readyClassId, setReadyClassId] = useState<string | null>(null);
  const reportContentReady = useCallback(() => setReadyClassId(classroomId), [classroomId]);
  const contentReady = readyClassId === classroomId;
  // The header AND the tab both reveal on this, so they flip together instead of racing their queries.
  const revealed = bannerReady && (!gatesContent || contentReady);

  const body = inlineTab ? (
    inlineTab.render(classroomId)
  ) : (
    <ClassDetailContent key={pathname}>{children}</ClassDetailContent>
  );

  return (
    <TeacherDashboardLayout
      header={revealed ? <DashboardHeader pathname={pathname} /> : <ClassHeaderSkeleton />}
      pathname={pathname}
    >
      <ClassContentReadyProvider onReady={reportContentReady} revealed={revealed}>
        {gatesContent || bannerReady ? body : <ClassDetailContentSkeleton />}
      </ClassContentReadyProvider>
      {inlineTab && bannerReady && (
        <ClassDetailEntryFlows classroomId={classroomId} key={`entry-${classroomId}`} />
      )}
    </TeacherDashboardLayout>
  );
};

// Chrome for /classes: the student my-assignments view and non-class paths keep legacy chrome;
// class pages get the dashboard shell (rebuilt tabs inline, the rest via ClassDetailContent).
const ClassroomsChromeWrapper = ({children, ...rest}: Props) => {
  const pathname: string = rest.location?.pathname ?? '';
  const classroomId = rest.params?.classId;

  const legacyChrome = (
    <>
      <SecondaryHeader {...rest} aria-label='secondary' />
      <Classrooms {...rest}>{children}</Classrooms>
    </>
  );

  if (!classroomId || STUDENT_ASSIGNMENTS_PATH.test(pathname)) {
    return legacyChrome;
  }

  // Leaving /classes: this layout lingers with a stale classId while the pathname already moved on,
  // briefly flashing the cross-class "All Classwork" view. Hold a spinner until we're gone. A
  // class-to-class switch stays in scope and renders normally; case-insensitive to match the router.
  if (!pathname.toLowerCase().startsWith('/classes/')) {
    return <LoadingIndicator />;
  }

  const inlineTab = INLINE_TABS.find(({match}) => match.test(pathname));

  return (
    <ClassDetailChrome classroomId={classroomId} inlineTab={inlineTab} pathname={pathname}>
      {children}
    </ClassDetailChrome>
  );
};

export default ClassroomsChromeWrapper;
